<template>
  <BaseDrawer
    no-padding
    height-auto-only
    no-toolbar
    no-bottom-rounding
    class="scrollbar-hide"
    close-label="settings"
    @close="handleCloseWithGuard"
  >
    <template #content>
      <div class="w-full flex flex-col" style="height: calc(100vh - 80px)">
        <div ref="statusTooltip" class="connection-tooltip" :style="{ opacity: statusTooltip ? 1 : 0 }">
          <BaseText size="sm" class="text-neutral-malpha-800">
            Connection {{ getLensConnectionsStatus?.[statusTooltip?.id]?.isConnected ? 'Active' : 'Issues' }} {{ getLensConnectionsStatus?.[statusTooltip?.id]?.isBackfill && getLensConnectionsStatus?.[statusTooltip?.id]?.isConnected ? '(Importing)' : '' }}
          </BaseText>
        </div>
        <!-- Toolbar -->
        <div class="w-full flex items-center h-14 justify-between px-4 py-3 border-b border-border-normal">
          <!-- Label -->
          <div class="flex items-center p-1.5 gap-1">
            <SettingsIcon class="text-icon-muted" />
            <BaseText type="label" size="sm" class="text-text-muted">
              Settings
            </BaseText>
          </div>
          <div class="flex items-center gap-2">
            <!-- Save Changes - conditionally shown based on hasUnsavedChanges -->
            <button v-if="getSettingsDrawerViewMode === 'lens'" class="black-btn-primary rounded-md text-white flex items-center gap-1 px-2 py-1.5"
            :class="{'opacity-90 cursor-not-allowed': !hasUnsavedChanges}"
            :disabled="!hasUnsavedChanges" @click="handleSaveChanges">
              <BaseText type="label" size="sm">
                Save Changes
              </BaseText>
            </button>
            <!-- Exit -->
            <button class="p-1.5" @click="handleCloseWithGuard">
              <TagRemoveIcon stroke="#5E6678" />
            </button>
          </div>
        </div>
        <div class="content-grid flex-grow h-full min-h-0">
          <!-- Sidebar navigation -->
          <div class="h-full flex flex-col gap-4 justify-between border-r border-border-normal p-4 overflow-y-auto">
            <!-- Navigation Options -->
            <div class="w-full flex flex-col gap-1">
              <BaseText type="heading" size="overline" class="text-text-subdued uppercase mb-2">
                Account & Team
              </BaseText>
              <div v-for="(option, index) in sidebarNavOptions" :key="`nav-option-${index}`" class="w-full relative">
                <button class="group nav-btn flex items-center gap-2 p-1.5 rounded-lg relative w-full"
                :class="{'active': getSettingsDrawerViewMode === option.viewMode, 'pointer-events-none': option.viewMode === 'lens-overview'}"
                @mouseenter="hoveredNavOption = option.viewMode" @mouseleave="hoveredNavOption = null"
                :disabled="preventNav(option)" @click="handleViewModeNavigation(option.viewMode)">
                  <component :is="option.icon" class="transition-colors duration-100"
                  :class="getSettingsDrawerViewMode === option.viewMode ? 'text-icon-muted' : 'text-icon-normal group-hover:text-icon-muted'"/>
                  <div class="flex-grow relative h-5" :class="preventNav(option) ? 'text-text-disabled' : 'text-text-muted'">
                    <transition name="nav-text">
                      <!-- Option Name -->
                      <BaseText v-if="!isNavHovered(option) || !preventNav(option)" type="label" size="sm" class="nav-text" key="option-name">
                        {{ option.name }}
                      </BaseText>
                      <!-- Blocked Nav tooltip -->
                      <BaseText v-else type="label" size="xs" class="nav-text w-max z-50" key="blocked-nav-tooltip">
                        {{ option?.blockedText || 'Admin only' }}
                      </BaseText>
                    </transition>
                  </div>
                  <!-- Disable icon -->
                  <div v-if="preventNav(option)" class="absolute flex items-center my-auto top-0 bottom-0 right-1">
                    <LockedIcon />
                  </div>
                </button>
              </div>
              <BaseText v-if="getSidebarLenses?.length" type="heading" size="overline" class="text-text-subdued uppercase my-2">
                Lens Brands
              </BaseText>
              <button v-for="(lens, index) in getSidebarLenses" :key="`lens-nav-option-${index}`"
              class="group nav-btn flex items-center gap-2 p-1.5 rounded-lg relative"
              :class="{'active': getSettingsDrawerViewMode === 'lens' && getCurrentRouteId == lens.id}"
              :disabled="preventNav(lens)" @click="handleLensSettingsNavigation(lens.id)">
                <div class="w-5 h-5 rounded-md flex items-center justify-center">
                  <img v-if="lens.avatar" :src="lens.avatar" class="w-5 h-5 object-cover rounded-md">
                  <div v-else class="w-5 h-5 flex items-center justify-center rounded-md bg-neutral-400">
                    <span class="text-background-hover text-label-xs font-medium" style="font-size: 10px;">
                      {{ getInitials(lens.name) }}
                    </span>
                  </div>
                </div>
                <BaseText type="label" size="sm" class="text-text-muted truncate flex-1 text-left">
                  {{ lens.name }}
                </BaseText>
                <div class="rounded-md p-0.5 w-5 h-5 flex items-center justify-center"
                :class="getLensConnectionsStatus?.[lens.id]?.isConnected ? 'bg-secondary-green-10' : 'bg-secondary-yellow-10'"
                @mouseenter="handleShowStatus(lens, true, $event)" @mouseleave="handleShowStatus(lens, false, $event)">
                  <ConnectionIcon
                    :class="getLensConnectionsStatus?.[lens.id]?.isConnected ? 'text-secondary-green-200' : 'text-secondary-yellow-200'"
                    :width="15"
                    :height="15"
                  />
                </div>
              </button>
            </div>
            <!-- Logout Button -->
            <button class="group logout-btn flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md"
            @click="handleLogout">
              <LogoutIcon class="text-icon-normal transition-colors duration-100 group-hover:text-secondary-red-200" />
              <BaseText type="label" size="sm" class="text-text-muted transition-colors duration-100 group-hover:text-secondary-red-200">
                Log Out
              </BaseText>
            </button>
          </div>
          <!-- Content View -->
          <div class="w-full h-full overflow-y-auto" style="scrollbar-gutter: stable;">
            <MyAccountSettings v-if="getSettingsDrawerViewMode === 'account'" />
            <PreferencesSettings v-if="getSettingsDrawerViewMode === 'preferences'" />
            <TeamSettings v-if="getSettingsDrawerViewMode === 'team'" />
            <AgencyDirectorySettings v-if="getSettingsDrawerViewMode === 'agency-directory'" />
            <WhiteLabelSettings
              v-if="getSettingsDrawerViewMode === 'whitelabel'"
              :current-team="getTeam"
              :current-user="getUser"
            />
            <ExpertProfileSettings
              v-if="getSettingsDrawerViewMode === 'expert'"
              :current-user="getUser"
            />
            <AffiliateReferralSettings v-if="getSettingsDrawerViewMode === 'referral'" />
            <PlanSettings
              v-if="getSettingsDrawerViewMode === 'plan'"
              @close="$emit('close')"
            />
            <BillingSettings v-if="getSettingsDrawerViewMode === 'billing'" />
            <IntegrationSettings v-if="getSettingsDrawerViewMode === 'integrations'" />
            <NotificationSettings v-if="getSettingsDrawerViewMode === 'notifications'" />
            <!-- <LensOverviewSettings v-if="getSettingsDrawerViewMode === 'lens-overview'" /> -->
            <LensSettings
              v-if="getSettingsDrawerViewMode === 'lens'"
              @close="$emit('close')"
            />
          </div>
        </div>
      </div>
    </template>
  </BaseDrawer>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'

// VIEW COMPONENTS
import MyAccountSettings from './MyAccountSettings.vue'
import PreferencesSettings from './PreferencesSettings.vue'
import TeamSettings from './team/TeamSettings.vue'
import AgencyDirectorySettings from './agency/AgencyDirectorySettings.vue'
import WhiteLabelSettings from './WhiteLabelSettings.vue'
import ExpertProfileSettings from './ExpertProfileSettings.vue'
import AffiliateReferralSettings from './AffiliateReferralSettings.vue'
import PlanSettings from './PlanSettings.vue'
import BillingSettings from './BillingSettings.vue'
import IntegrationSettings from './integration/IntegrationSettings.vue'
import NotificationSettings from './NotificationSettings.vue'
import LensSettings from './LensSettings.vue'
import LensOverviewSettings from './LensOverviewSettings.vue'
// ICONS
import ConnectionIcon from '../globals/Icons/ConnectionIcon.vue'
import LensIcon from '../globals/Icons/SidebarIcons/LensIcon.vue'
import SettingsIcon from '../globals/Icons/SettingsIcons/SettingsIcon.vue'
import TagRemoveIcon from '../globals/Icons/TagRemoveIcon.vue'
import LogoutIcon from '../globals/Icons/LogoutIcon.vue'
import LockedIcon from '../globals/Icons/LockedIcon.vue'
import * as ViewIcons from '../globals/Icons/SettingsIcons'

export default {
  name: 'SettingsDrawer',
  components: {
    MyAccountSettings,
    PreferencesSettings,
    TeamSettings,
    AgencyDirectorySettings,
    WhiteLabelSettings,
    ExpertProfileSettings,
    AffiliateReferralSettings,
    PlanSettings,
    BillingSettings,
    IntegrationSettings,
    LensSettings,
    NotificationSettings,
    LensOverviewSettings,
    SettingsIcon,
    TagRemoveIcon,
    LogoutIcon,
    LockedIcon,
    ConnectionIcon,
    LensIcon
  },
  data () {
    const sidebarNavOptions = [
      { name: 'My Account', icon: ViewIcons.MyAccountSettingsIcon, viewMode: 'account' },
      { name: 'Preferences', icon: ViewIcons.PreferencesSettingsIcon, viewMode: 'preferences' },
      { name: 'Team', icon: ViewIcons.TeamSettingsIcon, viewMode: 'team', blockedText: 'Requires subscription' },
      { name: 'Agency Directory', icon: ViewIcons.AgencyDirectorySettingsIcon, viewMode: 'agency-directory', blockedText: 'Requires subscription' },
      { name: 'Whitelabel', icon: ViewIcons.WhitelabelSettingsIcon, viewMode: 'whitelabel', blockedText: 'Requires team' },
      { name: 'Expert Profile', icon: ViewIcons.ExpertSettingsIcon, viewMode: 'expert', blockedText: 'Requires subscription' },
      { name: 'Referral', icon: ViewIcons.AffiliateReferralSettingsIcon, viewMode: 'referral', blockedText: 'Requires subscription' },
      { name: 'Integrations', icon: ViewIcons.IntegrationsSettingsIcon, viewMode: 'integrations' },
      { name: 'Plan', icon: ViewIcons.PlanSettingsIcon, viewMode: 'plan' },
      { name: 'Billing', icon: ViewIcons.BillingSettingsIcon, viewMode: 'billing', blockedText: 'Admin only' },
      // { name: 'Lens', icon: ViewIcons.LensSettingsIcon, viewMode: 'lens-overview' }
      // TODO: Notification settings will be hidden until we have notifications to send
      // { name: 'Notifications', icon: ViewIcons.NotificationsSettingsIcon, viewMode: 'notifications' }
    ]
    return {
      sidebarNavOptions,
      hasUnsavedChanges: false,
      statusTooltip: null,
      hoveredNavOption: null
    }
  },
  computed: {
    ...mapGetters('MiscModule', ['getSettingsDrawerViewMode', 'getSettingsDrawerSaveGuard']),
    ...mapGetters('LensModule', ['getSidebarLenses', 'getLensConnectionsStatus', 'getLenses']),
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'isTeamOwner', 'getGuestStatus']),
    ...mapGetters('RouterModule', ['getCurrentRouteId']),
    isGuestOnly () {
      return this.getGuestStatus?.isGuestOnly ?? false
    },
  },
  mounted () {
    // Listen for the lens-settings-has-changes event
    this.$root.$on('lens-settings-has-changes', this.handleChangesUpdate)
  },
  beforeDestroy () {
    // Clean up the event listener
    this.$root.$off('lens-settings-has-changes', this.handleChangesUpdate)
  },
  methods: {
    ...mapActions('AuthModule', ['logoutUser']),
    ...mapMutations('MiscModule', ['SET_SETTINGS_DRAWER_VIEW_MODE']),
    ...mapMutations('RouterModule', ['SET_CURRENT_ROUTE_ID']),
    ...mapMutations('LensModule', ['SET_SHOW_LENS_CREATION_MODAL']),
    handleViewModeNavigation (viewMode) {
      if (this.getSettingsDrawerSaveGuard) {
        const confirmed = window.confirm(this.getSettingsDrawerSaveGuard)
        if (!confirmed) return
      }
      this.SET_SETTINGS_DRAWER_VIEW_MODE(viewMode)
      this.SET_CURRENT_ROUTE_ID(null)
    },
    handleLensSettingsNavigation (lensId) {
      if (this.getSettingsDrawerSaveGuard) {
        const confirmed = window.confirm(this.getSettingsDrawerSaveGuard)
        if (!confirmed) return
      }
      this.SET_SETTINGS_DRAWER_VIEW_MODE('lens')
      this.SET_CURRENT_ROUTE_ID(lensId)
    },
    // Prevent navigation for the below cases
    preventNav (option) {
      switch (option.viewMode) {
        // If user isn't admin
        case 'team':
          return this.isGuestOnly
        case 'agency-directory':
          return this.isGuestOnly
        case 'whitelabel':
          return !this.getTeam
          case 'expert':
          return this.isGuestOnly
        case 'referral':
          return this.isGuestOnly
        case 'billing':
          return this.getTeam ? !this.isTeamOwner : false
        default:
          return false
      }
    },
    isNavHovered (option) {
      return this.hoveredNavOption === option.viewMode
    },
    handleShowStatus (lens, bool, event) {
      this.statusTooltip = lens
      const includeTooltip = this.$refs.statusTooltip
      if (includeTooltip && !bool) {
        includeTooltip.style.opacity = '0'
        this.statusTooltip = null
      } else {
        this.$nextTick(() => {
          const tooltipRect = includeTooltip.getBoundingClientRect()
          const referenceRect = event?.target?.getBoundingClientRect()
          const offset = { top: -78, left: -12 }
          const position = {
            top: referenceRect.top - tooltipRect.height - 12 + offset.top,
            left: referenceRect.left + (referenceRect.width / 2) - (tooltipRect.width / 2) + offset.left
          }
          includeTooltip.style.top = `${position.top}px`
          includeTooltip.style.left = `${position.left}px`
          // Add delay param later
          includeTooltip.style.opacity = '1'
        })
      }
    },
    handleLogout () {
      console.log('Logging out...')
      this.logoutUser()
    },
    handleCreateLens () {
      this.$router.push({ name: 'LensView' }).catch(err => {
        console.error('Error navigating to LensView:', err)
      })
      this.SET_SHOW_LENS_CREATION_MODAL(true)
      this.$emit('close')
    },
    // Add the getInitials method
    getInitials (name) {
      if (!name) return ''
      return name.split(' ').map(word => word[0]).join('').toUpperCase().substring(0, 2)
    },
    // Modified save method with console log for debugging
    handleSaveChanges () {
      console.log('Save button clicked in SettingsDrawer')

      // Get the current view mode to determine which component should handle the save
      const currentViewMode = this.getSettingsDrawerViewMode

      if (currentViewMode === 'lens') {
        console.log('Emitting save event for LensSettings')
        // Emit the event specifically for lens settings
        this.$root.$emit('lens-settings-save-clicked')
      } else {
        // Handle other view modes if needed
        console.log(`Emitting save event for ${currentViewMode}`)
        this.$root.$emit(`${currentViewMode}-settings-save-clicked`)
      }
    },
    // Add this new method to handle the changes event
    handleChangesUpdate (hasChanges) {
      console.log('Settings drawer received change update:', hasChanges)
      this.hasUnsavedChanges = hasChanges
    },
    handleCloseWithGuard () {
      if (this.getSettingsDrawerSaveGuard) {
        const confirmed = window.confirm(this.getSettingsDrawerSaveGuard)
        if (!confirmed) return
      }
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.content-grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  width: 100%;
}
.logout-btn {
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
  background-color: white;
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.logout-btn:hover {
  box-shadow: none;
  background-color: #FFF0F3;
}
.nav-btn {
  position: relative;
  box-shadow: none;
  background-color: transparent;
  transition: box-shadow 100ms ease-in-out, background-color 100ms ease-in-out;
}
.nav-btn:hover:not([disabled]) {
  box-shadow: 0px 1px 2px 0px rgba(0, 56, 108, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.nav-btn.active, .nav-btn.active:hover {
  box-shadow: none;
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.nav-btn:disabled{
  cursor: default !important;
}
.nav-btn:disabled > *{
  color: #a4abb8 !important;
}

.nav-text {
  width: max-content;
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
}

.connection-tooltip {
  position: fixed;
  background-color: rgba(6, 7, 15, 0.92);
  border-radius: 6px;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  white-space: nowrap;
  padding: 4px 12px;
  z-index: 99;
}

.nav-text-enter-active, .nav-text-leave-active {
  transition: opacity 100ms ease-in-out;
}
.nav-text-enter-from, .nav-text-enter, .nav-text-leave-to {
  opacity: 0;
}
.nav-text-enter-to, .nav-text-leave-from {
  opacity: 1;
}
</style>
